import { DeleteTwoTone, HomeTwoTone } from '@mui/icons-material';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CardContent,
  IconButton,
  List,
  ListItem,
  Link as MLink,
  Paper,
  Typography,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { deleteGuideTourHealthMedicine, guideTourHealthMedicine } from '../api';
import { AddEditGuidanceTourHealthMedicineAndDialysisReportDialog } from '../components/AddEditGuidanceTourHealthMedicineAndDialysisReportDialog';
import { PdfViewerDialog } from '../components/PdfViewerDialog';
import { AuthContext } from '../context/AuthContext';
import { useAlertSnackbar } from '../hooks/useAlertSnackbar';
import PageHeader from '../components/PageHeader';
import PdfViewer from '../components/PdfViewer';

export interface IGuideTourHealthMedicineInfo {
  salesIndividualId: number;
  customerFirstName?: string;
  customerLastName?: string;
  documentPath: string;
  documentId: number;
}

export function GuidanceTourHealthMedicinePage() {
  const { t } = useTranslation();
  const { token } = useContext(AuthContext);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { salesIndividualId } = useParams();
  const [searchParams] = useSearchParams();
  const tourId = searchParams.get('tourId');
  const [data, setData] = useState<IGuideTourHealthMedicineInfo[]>([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const showSnackbar = useAlertSnackbar();

  useEffect(() => {
    fetchGuideTourHealthMedicineReport(salesIndividualId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleAdd = async () => {
    setOpenDialog(true);
  };

  const handleSaveMedicineReport = async () => {
    setOpenDialog(false);
    await fetchGuideTourHealthMedicineReport(salesIndividualId);
  };

  const handleDelete = async (documentId?: number, customerId?: number) => {
    await deleteGuideTourHealthMedicineReport(
      documentId,
      salesIndividualId,
      customerId,
    );
    await fetchGuideTourHealthMedicineReport(salesIndividualId);
  };

  const deleteGuideTourHealthMedicineReport = async (
    documentId?: number,
    guideSalesIndividualId?: string,
    customerId?: number,
  ) => {
    try {
      const res = await deleteGuideTourHealthMedicine(
        documentId,
        token,
        guideSalesIndividualId,
        customerId,
      );

      if (!res.ok) {
        const errorResponse = await res.json();
        throw new Error(
          errorResponse.detail || `Request failed with status ${res.status}`,
        );
      }

      return res;
    } catch (e: any) {
      const errorMessage = e.message || 'Beklenmeyen bir hata oluştu.';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    }
  };

  const fetchGuideTourHealthMedicineReport = async (
    guideSalesIndividualId?: string,
  ) => {
    setLoading(true);
    try {
      const res = await guideTourHealthMedicine(token, guideSalesIndividualId);
      const data = await res.json();
      if (res?.status === 200) {
        setData(data);
      } else {
        throw data;
      }
    } catch (e: any) {
      setError(e.detail || t('loginPage.registerErrorMessage'));
    } finally {
      setLoading(false);
    }
  };

  if (!salesIndividualId) return null;

  const handleViewFİle = (documentPath: string) => {
    const fullPath = `https://portal.hisarturizm.com.tr${documentPath}`; //TODO
    setPdfUrl(fullPath);
    setPdfOpen(true);
  };

  return (
    <Box sx={{ maxWidth: 'lg' }}>
      <PageHeader
        title={t('guidanceTourHealth.title', { tourId })}
        actions={
          <Button
            onClick={() => handleAdd()}
            size="small"
            color="secondary"
            variant="contained"
          >
            {t('guidanceTourHealth.add')}
          </Button>
        }
      />

      <Breadcrumbs sx={{ mb: 2 }}>
        <MLink
          component={Link}
          to="/"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeTwoTone />
        </MLink>
        <MLink component={Link} to="/account">
          {t('nav.account')}
        </MLink>
        <MLink to="/account/guidance" component={Link}>
          <Typography>{t('nav.guidance')}</Typography>
        </MLink>
        <MLink
          component={Link}
          to={`/account/guidance/${salesIndividualId}?tourId=${tourId}`}
        >
          {t('nav.tourId', { tourId })}
        </MLink>
        <MLink
          component={Link}
          to={`/account/guidance/${salesIndividualId}/health?tourId=${tourId}`}
        >
          {t('guidanceTourHealth.title', { tourId })}
        </MLink>
        <Typography>{t('guidanceTourHealth.medicine')}</Typography>
      </Breadcrumbs>

      {!loading && data.length > 0 ? (
        <Paper>
          <List>
            {data.map((item, index) => (
              <ListItem
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 16px',
                  mt: index !== 0 ? -4 : 0,
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1">
                    {item.customerFirstName} {item.customerLastName}
                  </Typography>

                  <Button onClick={() => handleViewFİle(item.documentPath)}>
                    {t(
                      'guidanceTourHealth.medicineReportDialog.viewDocumentPath',
                    )}
                  </Button>
                </CardContent>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    color="error"
                    onClick={() =>
                      handleDelete(item.documentId, item.salesIndividualId)
                    }
                    aria-label="delete"
                  >
                    <DeleteTwoTone />
                  </IconButton>
                </Box>
              </ListItem>
            ))}
          </List>
        </Paper>
      ) : (
        <Alert severity="info">
          {t('guidanceTourHealth.medicineReportDialog.noData')}
        </Alert>
      )}

      <PdfViewer
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        pdfUrl={pdfUrl}
      />
      <AddEditGuidanceTourHealthMedicineAndDialysisReportDialog
        open={openDialog}
        onClose={handleDialogClose}
        onSave={handleSaveMedicineReport}
        guideSalesIndividualId={salesIndividualId}
      />
    </Box>
  );
}
